
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.3.0
 * @copyright  Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2023-03-23 (1.0.0) (jhoward)
 *      - <menu-item-group> handles displaying menu items for a menu section.
 *
 *    2023-04-21 (1.0.1) (jhoward)
 *      - Add the following allowed item components to <menu-item-group>: bimini-steak-item, bimini-food-item, bimini-cocktail-item, menu-item-text-only, menu-item-highlighted-box, high-limit-food-item.
 *      - <menu-item-group>::getMenuItemComponentOverride() will only use allowed menu item components.
 *
 *  2023-06-19 (1.1.0) (jhoward)
 *    - Support section.ms_show_pricing_header in <menu-item-group>.
 *
 *  2023-06-21 (1.1.1) (jhoward)
 *    - Default to 1 pricing type rather than 0 in <menu-item-group>.
 *    - Support <pancho-willies-cervezas-item> in <menu-item-group>.
 *
 *  2023-07-26 (1.2.0) (jhoward)
 *    - Support <chi-default-item> in <menu-item-group>.
 *    - Support <chi-wile-list-item> in <menu-item-group>.
 *    - Add `layout` prop to <menu-item-group>; support 'title-above' and '' values.
 *
 *  2023-08-28 (1.2.1) (jhoward)
 *    - Add VisualMenuItemGroupComponent interface.
 *
 *  2023-09-08 (1.2.2) (jhoward)
 *    - Add WV Steak House components to <menu-item-group>.
 *
 *  2023-10-05 (1.2.3) (jhoward)
 *    - Add <oceano-default-item> to <menu-item-group>.
 *
 *  2023-12-19 (1.2.4) (jhoward)
 *    - Add <back-bar-default-item> to <menu-item-group>.
 *
 *  2024-07-19 (1.2.5) (jhoward)
 *    - Refactor VisualMenuItemGroupComponent interface from <menu-item-group> to menu defs file.
 *
 *  2024-11-26 (1.2.6) (jhoward)
 *    - Add <thanksgiving-food-item> to <menu-item-group>.
 *
 *  2024-12-09 (1.2.7) (jhoward)
 *    - Add <new-years-food-item> to <menu-item-group>.
 *
 *  2024-12-13 (1.2.8) (jhoward)
 *    - Add <christmas-food-item> to <menu-item-group>.
 *    - Add <christmas-drink-item> to <menu-item-group>.
 *
 *  2025-02-10 (1.2.9) (jhoward)
 *    - Add <valentines-day-food-item> to <menu-item-group>.
 *
 *  2025-04-08 (1.3.0) (jhoward)
 *    - Remove "visual_" prefix from class names in <menu-item-group>.
 *    - Use <section-item-subgroup> in <menu-item-group>.
 *    - Remove "itemGroups" prop in <menu-item-group>.
 *    - Add "menuItemTagLocation" prop in <menu-item-group>.
 */

import {defineComponent, PropType} from "vue";
import {
  ALLOWED_ITEM_COMPONENTS, MENU_ITEM_GROUP_LAYOUT, MENU_ITEM_TAG_LOCATIONS,
  MenuSection, MenuTagModel,
} from "./defs";
import {
  computeItemGroupPricingTypes,
  methodGetPreviousGroupAndSubgroupTitles,
  methodIsSubgroupPricingShown
} from "../../helpers/menu-sections";
import SectionItemSubgroup from "./section/SectionItemSubgroup.vue";
import MenuTag from "./menu-tag.vue";

export default defineComponent({
  name: "menu-item-group",
  components: {
    MenuTag,
    SectionItemSubgroup
  },
  props: {
    menuItemComponent: {
      type: String,
      required: true,
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
    menuItemTagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: ''
    },
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    layout: {
      type: String as PropType<MENU_ITEM_GROUP_LAYOUT>,
      default: ''
    },
  },
  data() {
    return {
      ALLOWED_ITEM_COMPONENTS
    };
  },
  computed: {
    itemGroupPricingTypes: computeItemGroupPricingTypes,
    menuItemComponentOverride(): string {
      return this.ALLOWED_ITEM_COMPONENTS.includes(this.menuItemComponent) ? this.menuItemComponent : 'default-item';
    },
    dummyContainsNutsMenuTag(): MenuTagModel {
      return {mt_id: 0, mt_title: 'Contains Nuts', mt_icon_class: 'tag-contains-nuts'};
    }
  },
  methods: {
    isSubgroupPricingShown: methodIsSubgroupPricingShown,
    getPreviousGroupAndSubgroupTitles: methodGetPreviousGroupAndSubgroupTitles,
  }
})
